import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  EyeOff,
  Globe,
  MapPinPlus,
  RotateCcw,
  Search,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Card, CardContent, CardHeader } from '@/components/ui';
import {
  gisApi,
  type BatchGeocodeResult,
  type FailedGeocodeCandidate,
  type GeocodeFailureOutcome,
} from '@/lib/api';
import type { GISEntitySuggestion } from '@/lib/api';

const OUTCOME_TONE: Record<GeocodeFailureOutcome, string> = {
  no_match: 'bg-slate-100 text-slate-700',
  low_confidence: 'bg-amber-100 text-amber-800',
  failed_api: 'bg-red-100 text-red-700',
};

interface Props {
  /** Lifts a candidate into the parent's EntityPicker so the analyst can edit. */
  onAddManually: (entity: GISEntitySuggestion) => void;
  /**
   * Opens the geocode tab pre-filled with `query` so the analyst can
   * refine the Nominatim search (e.g. "Cadiz Solar Project, Spain"
   * → "Andalusia, Spain"). Distinct from `onAddManually`, which goes
   * to the coords-by-hand flow.
   */
  onSearchAddress: (entity: GISEntitySuggestion, query: string) => void;
}

/**
 * Lists entities the batch geocoder couldn't place. Each row offers three
 * follow-ups: add coords manually (selects the entity in the picker above),
 * retry on the next batch run, or dismiss permanently.
 */
export function GISFailedCandidates({ onAddManually, onSearchAddress }: Props) {
  const { t } = useTranslation(['studio', 'common']);
  const qc = useQueryClient();
  const [includeDismissed, setIncludeDismissed] = useState(false);
  const [bulkResult, setBulkResult] = useState<BatchGeocodeResult | null>(null);
  // IRIs picked via the per-row checkboxes. Cleared whenever the
  // underlying list changes (data is invalidated by any action).
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ['studio', 'gis', 'failed-candidates', includeDismissed],
    queryFn: () => gisApi.failedCandidates(50, includeDismissed),
    staleTime: 15_000,
  });

  const visibleIris = useMemo(
    () => (data?.items ?? []).map((r) => r.entity_iri),
    [data],
  );
  const selectedArr = useMemo(
    () => visibleIris.filter((iri) => selected.has(iri)),
    [visibleIris, selected],
  );
  const allSelected = visibleIris.length > 0 && selectedArr.length === visibleIris.length;

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['studio', 'gis', 'failed-candidates'] });
    void qc.invalidateQueries({ queryKey: ['studio', 'gis', 'geocode-candidates'] });
    // The map cache too, so newly geocoded entities show up immediately.
    void qc.invalidateQueries({ queryKey: ['map', 'entities'] });
    setSelected(new Set());
  };

  const dismiss = useMutation({
    mutationFn: (iri: string) => gisApi.dismissAttempt(iri),
    onSuccess: invalidate,
  });
  const retry = useMutation({
    mutationFn: (iri: string) => gisApi.retryAttempt(iri),
    onSuccess: invalidate,
  });
  const bulkRetry = useMutation({
    mutationFn: (iris: string[] | undefined) =>
      gisApi.geocodeFailed(iris ? { iris } : {}),
    onSuccess: (res) => {
      setBulkResult(res);
      invalidate();
    },
  });
  const bulkDismiss = useMutation({
    mutationFn: (iris: string[]) => gisApi.bulkDismissAttempts(iris),
    onSuccess: invalidate,
  });
  const bulkResetRetry = useMutation({
    mutationFn: (iris: string[]) => gisApi.bulkRetryAttempts(iris),
    onSuccess: invalidate,
  });

  // ETA upfront — Nominatim's 1 req/s policy + a fallback per failure
  // means worst case ~2.2s per candidate. Be honest about the wait.
  const pendingCount = data?.total ?? 0;
  const bulkScopeCount = selectedArr.length > 0 ? selectedArr.length : pendingCount;
  const bulkEtaSec = bulkScopeCount > 0 ? Math.ceil(bulkScopeCount * 2.2) : 0;
  const anyBusy =
    bulkRetry.isPending || bulkDismiss.isPending || bulkResetRetry.isPending;

  if (!isLoading && (!data || data.total === 0)) {
    // Don't render an empty card if there's nothing to act on.
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden />
            <h2 className="text-h2 text-brand-900">
              {t('studio:gis.failedCandidates.title', 'Geocode failures awaiting manual review')}
            </h2>
          </div>
          <label className="flex items-center gap-2 text-small text-slate-600">
            <input
              type="checkbox"
              checked={includeDismissed}
              onChange={(e) => setIncludeDismissed(e.target.checked)}
            />
            {t('studio:gis.failedCandidates.includeDismissed', 'Include dismissed')}
          </label>
        </div>
        <p className="mt-1 text-small text-slate-600">
          {t(
            'studio:gis.failedCandidates.subtitle',
            'These entities are hidden from the auto-geocoder. Add coordinates manually, retry on the next batch, or dismiss to keep them hidden.',
          )}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setBulkResult(null);
              bulkRetry.mutate(
                selectedArr.length > 0 ? selectedArr : undefined,
              );
            }}
            disabled={anyBusy || bulkScopeCount === 0}
            title={t(
              'studio:gis.failedCandidates.bulkAddressTitle',
              'Re-run address geocoding (primary + prefix fallback) on the selected rows — or every pending failure when nothing is selected',
            )}
          >
            <Globe className="mr-1 h-3.5 w-3.5" aria-hidden />
            {bulkRetry.isPending
              ? t('studio:gis.failedCandidates.bulkRunning', {
                  n: bulkScopeCount,
                  eta: bulkEtaSec,
                  defaultValue: 'Geocoding {{n}} candidates — ~{{eta}}s…',
                })
              : selectedArr.length > 0
                ? t('studio:gis.failedCandidates.bulkAddressSelected', {
                    n: selectedArr.length,
                    defaultValue: 'Retry {{n}} selected by address',
                  })
                : t('studio:gis.failedCandidates.bulkAddress', {
                    n: pendingCount,
                    defaultValue: 'Retry all {{n}} by address',
                  })}
          </Button>
          {selectedArr.length > 0 && (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => bulkResetRetry.mutate(selectedArr)}
                disabled={anyBusy}
                title={t(
                  'studio:gis.failedCandidates.bulkRetry',
                  'Drop the selected attempts so the auto-pool picks them up again on the next batch',
                )}
              >
                <RotateCcw className="mr-1 h-3.5 w-3.5" aria-hidden />
                {t('studio:gis.failedCandidates.bulkRetryShort', {
                  n: selectedArr.length,
                  defaultValue: 'Reset {{n}} for next batch',
                })}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => bulkDismiss.mutate(selectedArr)}
                disabled={anyBusy}
                title={t(
                  'studio:gis.failedCandidates.bulkDismiss',
                  'Hide every selected row permanently',
                )}
              >
                <EyeOff className="mr-1 h-3.5 w-3.5" aria-hidden />
                {t('studio:gis.failedCandidates.bulkDismissShort', {
                  n: selectedArr.length,
                  defaultValue: 'Dismiss {{n}}',
                })}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelected(new Set())}
                disabled={anyBusy}
                title={t('studio:gis.failedCandidates.clearSelection', 'Clear selection')}
              >
                <X className="mr-1 h-3.5 w-3.5" aria-hidden />
                {t('studio:gis.failedCandidates.clearSelectionShort', 'Clear')}
              </Button>
            </>
          )}
          {bulkResult && (
            <span className="text-small text-slate-600">
              {t('studio:gis.failedCandidates.bulkResult', {
                geocoded: bulkResult.geocoded,
                no_match: bulkResult.skipped_no_match,
                low_conf: bulkResult.skipped_low_confidence,
                failed: bulkResult.failed,
                defaultValue:
                  '{{geocoded}} geocoded · {{no_match}} no match · {{low_conf}} low conf · {{failed}} API errors',
              })}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <p className="text-small text-slate-500">{t('common:status.loading')}</p>
        )}
        {!isLoading && data && (
          <div className="rounded border border-slate-200">
            <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-small text-slate-600">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) {
                    el.indeterminate = selectedArr.length > 0 && !allSelected;
                  }
                }}
                onChange={(e) =>
                  setSelected(
                    e.target.checked ? new Set(visibleIris) : new Set(),
                  )
                }
                aria-label="Select all"
              />
              <span>
                {selectedArr.length > 0
                  ? t('studio:gis.failedCandidates.nSelected', {
                      n: selectedArr.length,
                      defaultValue: '{{n}} selected',
                    })
                  : t('studio:gis.failedCandidates.selectAllHint', 'Select all')}
              </span>
            </div>
            <ul className="divide-y divide-slate-100">
              {data.items.map((row) => {
                const entity = {
                  entity_iri: row.entity_iri,
                  label: row.label,
                  entity_class: row.entity_class,
                };
                return (
                  <FailedRow
                    key={row.entity_iri}
                    row={row}
                    selected={selected.has(row.entity_iri)}
                    onSelectChange={(checked) =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (checked) next.add(row.entity_iri);
                        else next.delete(row.entity_iri);
                        return next;
                      })
                    }
                    onAddManually={() => onAddManually(entity)}
                    onSearchAddress={() => onSearchAddress(entity, row.label)}
                    onDismiss={() => dismiss.mutate(row.entity_iri)}
                    onRetry={() => retry.mutate(row.entity_iri)}
                    busy={dismiss.isPending || retry.isPending || anyBusy}
                  />
                );
              })}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface RowProps {
  row: FailedGeocodeCandidate;
  selected: boolean;
  onSelectChange: (checked: boolean) => void;
  onAddManually: () => void;
  onSearchAddress: () => void;
  onDismiss: () => void;
  onRetry: () => void;
  busy: boolean;
}

function FailedRow({
  row,
  selected,
  onSelectChange,
  onAddManually,
  onSearchAddress,
  onDismiss,
  onRetry,
  busy,
}: RowProps) {
  const { t } = useTranslation('studio');
  const shortClass = row.entity_class.split(/[#/]/).pop() ?? row.entity_class;
  const importanceLabel =
    row.outcome === 'low_confidence' && row.importance_score != null
      ? ` (${row.importance_score.toFixed(2)})`
      : '';

  return (
    <li className="flex flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center">
      <input
        type="checkbox"
        checked={selected}
        onChange={(e) => onSelectChange(e.target.checked)}
        aria-label={`Select ${row.label}`}
        className="shrink-0 self-start sm:self-center"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-medium text-brand-900" title={row.label}>
            {row.label}
          </p>
          <Badge variant="neutral">{shortClass}</Badge>
          <span
            className={`rounded px-1.5 py-0.5 text-small font-mono ${OUTCOME_TONE[row.outcome]}`}
            title={t(`gis.failedCandidates.outcomeHint.${row.outcome}`, '')}
          >
            {row.outcome}
            {importanceLabel}
          </span>
          {row.retry_count > 1 && (
            <span className="text-small text-slate-500">×{row.retry_count}</span>
          )}
        </div>
        <p
          className="mt-0.5 truncate font-mono text-small text-slate-500"
          title={row.entity_iri}
        >
          {row.entity_iri}
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap gap-1.5 sm:justify-end">
        <Button
          size="sm"
          variant="secondary"
          onClick={onSearchAddress}
          disabled={busy}
          title={t(
            'gis.failedCandidates.searchAddress',
            'Open the geocode tab with this label pre-filled',
          )}
        >
          <Search className="mr-1 h-3.5 w-3.5" aria-hidden />
          {t('gis.failedCandidates.searchAddressShort', 'Address')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onAddManually}
          disabled={busy}
          title={t('gis.failedCandidates.addManually', 'Add coordinates manually')}
        >
          <MapPinPlus className="mr-1 h-3.5 w-3.5" aria-hidden />
          {t('gis.failedCandidates.addManuallyShort', 'Manual')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onRetry}
          disabled={busy}
          title={t('gis.failedCandidates.retry', 'Reset for the next auto batch')}
        >
          <RotateCcw className="mr-1 h-3.5 w-3.5" aria-hidden />
          {t('gis.failedCandidates.retryShort', 'Retry')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onDismiss}
          disabled={busy}
          title={t('gis.failedCandidates.dismiss', 'Hide permanently')}
        >
          <EyeOff className="mr-1 h-3.5 w-3.5" aria-hidden />
          {t('gis.failedCandidates.dismissShort', 'Dismiss')}
        </Button>
      </div>
    </li>
  );
}
